import { useRef, type CSSProperties } from 'react'
import { useInView } from '@/hooks/useInView'
import { cx } from '@/lib/cx'
import styles from './PrismLight.module.css'

/** Where the light pools inside its positioned parent. */
export type PrismLightPlacement =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

/** Props for {@link PrismLight}. */
export interface PrismLightProps {
  /** Overall strength, 0..1. Default `0.9`. */
  intensity?: number
  /** Where the light pools. Default `'center'`. */
  placement?: PrismLightPlacement
  className?: string
}

/**
 * Soft iridescent light for glass to refract. Four blurred prism-tint blobs drift very
 * slowly behind your content: CSS transforms only, zero JS per frame, paused under reduced
 * motion. Frosted glass is invisible on flat white; drop this behind any `.glass` surface.
 *
 * It fills its nearest positioned ancestor (`position: absolute; inset: 0`) and is
 * decorative (`aria-hidden`). Put your content after it, or give the content a higher
 * `z-index`.
 *
 * @example
 * <div className={styles.cardWrap}>
 *   <PrismLight placement="top-right" intensity={0.8} />
 *   <article className={cx('glass', styles.card)}>…</article>
 * </div>
 */
export function PrismLight({ intensity = 0.9, placement = 'center', className }: PrismLightProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const visible = useInView(rootRef, { rootMargin: '10% 0px' })
  const style = { '--prism-intensity': intensity } as CSSProperties
  return (
    <div
      ref={rootRef}
      className={cx(styles.root, className)}
      style={style}
      data-visible={visible}
      aria-hidden="true"
    >
      <div className={styles.field} data-placement={placement}>
        <span className={cx(styles.blob, styles.lilac)} />
        <span className={cx(styles.blob, styles.sky)} />
        <span className={cx(styles.blob, styles.mint)} />
        <span className={cx(styles.blob, styles.blush)} />
      </div>
    </div>
  )
}
